import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import './StudyMissionPlayer.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Componente para resolver Missão de Estudo (Criança)
 */
function StudyMissionPlayer() {
  const { missionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isReadOnly = searchParams.get('readonly') === 'true';
  
  const [loading, setLoading] = useState(true);
  const [mission, setMission] = useState(null);
  const [simulations, setSimulations] = useState([]);
  const [error, setError] = useState('');
  const [generatingSimulation, setGeneratingSimulation] = useState(false);
  
  const [currentStep, setCurrentStep] = useState('summary'); // summary, activities, simulation, report
  const [activityAnswers, setActivityAnswers] = useState({});
  const [summaryStartedAt, setSummaryStartedAt] = useState(null);
  const [activitiesStartedAt, setActivitiesStartedAt] = useState(null);
  const [simulationAnswers, setSimulationAnswers] = useState({});
  const [selectedSimulation, setSelectedSimulation] = useState(null);
  const [simulationStartedAt, setSimulationStartedAt] = useState(null);
  const [showSimulationResult, setShowSimulationResult] = useState(false);
  const [simulationResult, setSimulationResult] = useState(null);
  const [showActivityResult, setShowActivityResult] = useState(false);
  const [activityResult, setActivityResult] = useState(null);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [printOptions, setPrintOptions] = useState({
    summary: true,
    activities: true,
    simulations: true
  });
  
  useEffect(() => {
    loadMission();
    loadSimulations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionId]);

  useEffect(() => {
    if (!mission) return;

    if (currentStep === 'summary' && !mission.summary_completed && !summaryStartedAt) {
      setSummaryStartedAt(Date.now());
    }

    if (currentStep === 'activities' && !mission.activities_completed && !activitiesStartedAt) {
      setActivitiesStartedAt(Date.now());
    }
  }, [currentStep, mission, summaryStartedAt, activitiesStartedAt]);
  
  const loadMission = async () => {
    console.log('🔍 [StudyMissionPlayer] loadMission() iniciado');
    console.log('   missionId:', missionId);
    console.log('   API_URL:', API_URL);
    
    try {
      const token = localStorage.getItem('access_token');
      console.log('   Token:', token ? `${token.substring(0, 20)}...` : 'NULL');
      
      const url = `${API_URL}/study-missions/${missionId}`;
      console.log('   URL completa:', url);
      
      console.log('🌐 Fazendo requisição GET...');
      const response = await axios.get(
        url,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('✅ Resposta recebida:', response.status);
      console.log('   Data keys:', Object.keys(response.data));
      console.log('   Mission:', response.data.study_mission ? 'OK' : 'NULL');
      
      setMission(response.data.study_mission);
      console.log('✅ Mission setada no state');
      
      // Log detalhado das atividades
      if (response.data.study_mission?.activities) {
        console.log('📝 ATIVIDADES CARREGADAS:');
        console.log('   Total:', response.data.study_mission.activities.length);
        response.data.study_mission.activities.forEach((act, idx) => {
          const content = act.question || act.statement || act.sentence;
          console.log(`   Atividade ${idx + 1}:`, {
            id: act.id,
            type: act.type,
            hasContent: !!content,
            contentLength: content ? content.length : 0,
            contentPreview: content ? content.substring(0, 50) + '...' : 'VAZIO'
          });
        });
      } else {
        console.log('⚠️ Nenhuma atividade encontrada na missão');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar missão:', err);
      console.error('   Message:', err.message);
      console.error('   Response:', err.response?.data);
      console.error('   Status:', err.response?.status);
      setError('Erro ao carregar missão');
    } finally {
      console.log('🏁 loadMission() finalizado');
      setLoading(false);
    }
  };
  
  const loadSimulations = async () => {
    try {
      const token = localStorage.getItem('access_token'); // Corrigido
      const response = await axios.get(
        `${API_URL}/study-missions/${missionId}/simulations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Simulados carregados:', response.data.simulations);
      
      // Log detalhado das questões
      if (response.data.simulations && response.data.simulations.length > 0) {
        response.data.simulations.forEach((sim, idx) => {
          console.log(`Simulado ${idx + 1}:`, {
            id: sim.id,
            difficulty: sim.difficulty,
            status: sim.status,
            total_questions: sim.total_questions,
            questions: sim.questions,
            questions_length: sim.questions ? sim.questions.length : 0,
            first_question: sim.questions && sim.questions.length > 0 ? sim.questions[0] : null
          });
        });
      }
      
      setSimulations(response.data.simulations);
    } catch (err) {
      console.error('Erro ao carregar simulados:', err);
    }
  };
  
  const handleCompleteSummary = async () => {
    try {
      const elapsedMs = summaryStartedAt ? Math.max(0, Date.now() - summaryStartedAt) : 0;
      const elapsedMinutes = Math.max(1, Math.ceil(elapsedMs / 60000));

      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/study-missions/${missionId}/summary/complete`,
        { time_spent_minutes: elapsedMinutes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Parabéns! Você ganhou ${response.data.points_awarded} HeroCoins!`);
      loadMission();
      setCurrentStep('activities');
      setSummaryStartedAt(null);
      setActivitiesStartedAt(Date.now());
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao completar resumo');
    }
  };
  
  const handleSubmitActivities = async () => {
    try {
      const elapsedMs = activitiesStartedAt ? Math.max(0, Date.now() - activitiesStartedAt) : 0;
      const elapsedMinutes = Math.max(1, Math.ceil(elapsedMs / 60000));

      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/study-missions/${missionId}/activities/submit`,
        {
          answers: activityAnswers,
          time_spent_minutes: elapsedMinutes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Armazena o resultado completo para exibição detalhada
      setActivityResult(response.data);
      setShowActivityResult(true);
      
      // Recarrega dados
      loadMission();
      setActivitiesStartedAt(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao submeter atividades');
    }
  };
  
  const handleCloseActivityResult = () => {
    setShowActivityResult(false);
    setCurrentStep('simulation');
  };
  
  const handleStartSimulation = (simulation) => {
    console.log('Simulado selecionado:', simulation);
    console.log('Questões do simulado:', simulation.questions);
    console.log('Status do simulado:', simulation.status);
    console.log('Respostas salvas:', simulation.answers);
    
    setSelectedSimulation(simulation);
    
    // Se o simulado está completo, mostra o resultado diretamente
    if (simulation.status === 'COMPLETED') {
      // Carrega as respostas para exibir o feedback
      if (simulation.answers) {
        setSimulationAnswers(simulation.answers);
      }
      
      // Monta o resultado para exibição
      const result = {
        score: simulation.score || 0,
        correct_count: simulation.correct_count || 0,
        total_questions: simulation.total_questions || simulation.questions.length,
        feedback: simulation.questions.map((question, idx) => {
          const userAnswer = simulation.answers ? (simulation.answers[question.id] || simulation.answers[String(question.id)]) : null;
          return {
            question_id: question.id,
            question: question.question,
            type: question.type,
            options: question.options,
            user_answer: userAnswer,
            correct_answer: question.correct_answer,
            explanation: question.explanation,
            is_correct: checkAnswer(question, userAnswer)
          };
        })
      };
      
      setSimulationResult(result);
      setShowSimulationResult(true);
      setSimulationStartedAt(null);
    } else {
      setSimulationAnswers({});
      setShowSimulationResult(false);
      setSimulationResult(null);
      setSimulationStartedAt(Date.now());
    }
    
    setCurrentStep('simulation');
  };
  
  // Função auxiliar para verificar resposta
  const checkAnswer = (question, userAnswer) => {
    if (!userAnswer) return false;
    
    const correctAnswer = question.correct_answer;
    
    if (question.type === 'MULTIPLE_CHOICE') {
      return String(userAnswer).trim().toUpperCase() === String(correctAnswer).trim().toUpperCase();
    } else if (question.type === 'TRUE_FALSE') {
      const userBool = typeof userAnswer === 'boolean' ? userAnswer : String(userAnswer).toLowerCase() === 'true';
      const correctBool = typeof correctAnswer === 'boolean' ? correctAnswer : String(correctAnswer).toUpperCase() === 'TRUE';
      return userBool === correctBool;
    } else if (question.type === 'COMPLETE') {
      const userText = String(userAnswer).trim().toLowerCase();
      const correctText = String(correctAnswer).trim().toLowerCase();
      return userText === correctText || correctText.includes(userText) || userText.includes(correctText);
    }
    
    return false;
  };

  const getMultipleChoiceOptionText = (question, answer) => {
    if (!question?.options || answer === null || answer === undefined) return null;

    const normalizedAnswer = String(answer).trim().toUpperCase();
    if (!normalizedAnswer) return null;

    const optionByPrefix = question.options.find((opt) => {
      const optionText = String(opt || '').trim();
      const prefixMatch = optionText.match(/^([A-Z])[).:\-\s]/i);
      if (!prefixMatch) return false;
      return prefixMatch[1].toUpperCase() === normalizedAnswer;
    });

    if (optionByPrefix) return String(optionByPrefix).trim();

    const answerIndex = normalizedAnswer.charCodeAt(0) - 65;
    if (answerIndex >= 0 && answerIndex < question.options.length) {
      return String(question.options[answerIndex] || '').trim() || null;
    }

    return null;
  };

  const formatSimulationFeedbackAnswer = (question, answer) => {
    if (answer === null || answer === undefined || String(answer).trim() === '') {
      return '(não respondida)';
    }

    if (question?.type === 'MULTIPLE_CHOICE') {
      const optionText = getMultipleChoiceOptionText(question, answer);
      if (optionText) {
        return `${String(answer).toUpperCase()} — ${optionText}`;
      }
      return String(answer).toUpperCase();
    }

    if (question?.type === 'TRUE_FALSE') {
      const boolValue = typeof answer === 'boolean' ? answer : String(answer).toLowerCase() === 'true';
      return boolValue ? 'Verdadeiro' : 'Falso';
    }

    return String(answer);
  };
  
  const handleSubmitSimulation = async () => {
    try {
      const elapsedMs = simulationStartedAt ? Math.max(0, Date.now() - simulationStartedAt) : 0;
      const elapsedMinutes = Math.max(1, Math.ceil(elapsedMs / 60000));

      console.log('📤 Enviando respostas do simulado:');
      console.log('  Simulado ID:', selectedSimulation.id);
      console.log('  Respostas:', JSON.stringify(simulationAnswers, null, 2));
      console.log('  Quantidade de respostas:', Object.keys(simulationAnswers).length);
      console.log('  Tempo (min):', elapsedMinutes);
      
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/study-missions/${missionId}/simulations/${selectedSimulation.id}/submit`,
        { 
          answers: simulationAnswers,
          time_spent_minutes: elapsedMinutes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('📥 Resposta do servidor:', JSON.stringify(response.data, null, 2));
      console.log('📋 Feedback detalhado:');
      response.data.feedback?.forEach((f, i) => {
        console.log(`  Q${i+1}: user="${f.user_answer}" correct="${f.correct_answer}" is_correct=${f.is_correct}`);
      });
      
      // Armazena o resultado completo
      setSimulationResult(response.data);
      setShowSimulationResult(true);
      setSimulationStartedAt(null);
      
      // Recarrega dados
      loadMission();
      loadSimulations();
    } catch (err) {
      console.error('❌ Erro ao submeter simulado:', err);
      alert(err.response?.data?.error || 'Erro ao submeter simulado');
    }
  };
  
  const handleCloseSimulationResult = () => {
    setShowSimulationResult(false);
    setSimulationResult(null);
    setSelectedSimulation(null);
    setSimulationAnswers({});
    setSimulationStartedAt(null);
  };
  
  const handleGenerateNewSimulation = async (difficulty) => {
    try {
      setGeneratingSimulation(true);
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/study-missions/${missionId}/simulations/generate`,
        { difficulty },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Novo simulado gerado!');
      await loadSimulations();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao gerar simulado');
    } finally {
      setGeneratingSimulation(false);
    }
  };
  
  const handleCompleteMission = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/study-missions/${missionId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Missão concluída! Total: ${response.data.total_points_earned} pontos conquistados!`);
      navigate('/child-dashboard');
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao completar missão');
    }
  };

  const escapeHtml = (value) => {
    const input = value === null || value === undefined ? '' : String(value);
    return input
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const getQuestionText = (item) => {
    return item?.question || item?.statement || item?.sentence || '';
  };

  const getQuestionOptions = (item) => {
    if (Array.isArray(item?.options) && item.options.length > 0) {
      return item.options;
    }
    if (Array.isArray(item?.alternatives) && item.alternatives.length > 0) {
      return item.alternatives;
    }
    return [];
  };

  const togglePrintOption = (key) => {
    setPrintOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const buildPrintableHtml = () => {
    const sections = [];

    if (printOptions.summary) {
      sections.push(`
        <section>
          <h2>📖 Conteúdo</h2>
          <div class="print-block">${escapeHtml(mission.generated_summary || 'Conteúdo ainda não disponível.').replace(/\n/g, '<br/>')}</div>
        </section>
      `);
    }

    if (printOptions.activities) {
      const activities = Array.isArray(mission.activities) ? mission.activities : [];
      const activitiesHtml = activities.length
        ? activities.map((activity, idx) => {
            const options = getQuestionOptions(activity);
            const optionsHtml = options.length
              ? `<ul>${options.map((opt) => `<li>${escapeHtml(opt)}</li>`).join('')}</ul>`
              : '';
            return `
              <article class="print-item">
                <h3>Questão ${idx + 1}</h3>
                <p>${escapeHtml(getQuestionText(activity))}</p>
                ${optionsHtml}
              </article>
            `;
          }).join('')
        : '<p>Atividade avaliativa ainda não disponível.</p>';

      sections.push(`
        <section>
          <h2>✏️ Atividade Avaliativa</h2>
          ${activitiesHtml}
        </section>
      `);
    }

    if (printOptions.simulations) {
      const simulationsHtml = simulations.length
        ? simulations.map((simulation, simIdx) => {
            const questions = Array.isArray(simulation.questions) ? simulation.questions : [];
            const questionsHtml = questions.length
              ? questions.map((question, qIdx) => {
                  const options = getQuestionOptions(question);
                  const optionsHtml = options.length
                    ? `<ul>${options.map((opt) => `<li>${escapeHtml(opt)}</li>`).join('')}</ul>`
                    : '';
                  return `
                    <article class="print-item">
                      <h4>Questão ${qIdx + 1}</h4>
                      <p>${escapeHtml(getQuestionText(question))}</p>
                      ${optionsHtml}
                    </article>
                  `;
                }).join('')
              : '<p>Questões ainda não disponíveis.</p>';

            return `
              <div class="simulation-block">
                <h3>Simulado ${simIdx + 1} (${escapeHtml(simulation.difficulty || 'Nível não definido')})</h3>
                ${questionsHtml}
              </div>
            `;
          }).join('')
        : '<p>Nenhum simulado gerado até o momento.</p>';

      sections.push(`
        <section>
          <h2>🎯 Simulados</h2>
          ${simulationsHtml}
        </section>
      `);
    }

    return `
      <!doctype html>
      <html lang="pt-BR">
      <head>
        <meta charset="utf-8" />
        <title>Impressão - ${escapeHtml(mission.title)}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 24px; color: #222; }
          h1 { margin-bottom: 6px; }
          .meta { margin-bottom: 20px; color: #555; }
          section { margin-bottom: 24px; page-break-inside: auto; break-inside: auto; }
          section + section { page-break-before: auto; break-before: auto; }
          .print-block { line-height: 1.6; background: #fafafa; border: 1px solid #ddd; border-radius: 8px; padding: 12px; }
          .print-item { border: 1px solid #ddd; border-radius: 8px; padding: 10px; margin: 10px 0; page-break-inside: avoid; break-inside: avoid; }
          .simulation-block { margin: 12px 0 20px; }
          ul { margin: 8px 0 0 20px; }
          @media print {
            body { margin: 12mm; }
            h1, .meta { page-break-after: avoid; break-after: avoid; }
            section:first-of-type { page-break-before: auto; break-before: auto; }
          }
        </style>
      </head>
      <body>
        <h1>📘 ${escapeHtml(mission.title)}</h1>
        <div class="meta">Matéria: ${escapeHtml(mission.subject || '-')} • Data: ${new Date().toLocaleDateString('pt-BR')}</div>
        ${sections.join('')}
      </body>
      </html>
    `;
  };

  const handlePrintMission = () => {
    if (!printOptions.summary && !printOptions.activities && !printOptions.simulations) {
      alert('Selecione pelo menos uma seção para imprimir.');
      return;
    }

    const html = buildPrintableHtml();
    const printWindow = window.open('', '_blank', 'width=900,height=700');

    if (!printWindow) {
      alert('Não foi possível abrir a janela de impressão. Verifique o bloqueador de pop-ups.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };
  
  if (loading) {
    return (
      <div className="study-mission-player">
        <div className="mission-loading-container">
          <div className="mission-loading-spinner"></div>
          <p>Carregando missão...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="study-mission-player">
        <div className="error-container">
          <h2>❌ Erro</h2>
          <p>{error}</p>
          <button className="btn-primary" onClick={() => navigate(isReadOnly ? '/parent-dashboard' : '/child')}>
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  if (!mission) {
    return (
      <div className="study-mission-player">
        <div className="error-container">
          <h2>📘 Missão não encontrada</h2>
          <button className="btn-primary" onClick={() => navigate(isReadOnly ? '/parent-dashboard' : '/child')}>
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="study-mission-player">
      <div className="study-mission-container">
        <div className="study-mission-header">
          {isReadOnly && (
            <div style={{
              backgroundColor: '#FFA726',
              color: 'white',
              padding: '12px 20px',
              borderRadius: '8px',
              marginBottom: '10px',
              textAlign: 'center',
              fontWeight: '600',
              lineHeight: '1.5'
            }}>
              👁️ Modo Visualização - Você pode navegar pelo conteúdo e criar simulados, mas não pode responder atividades
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h1 style={{ margin: 0 }}>📘 {mission.title}</h1>
            <button 
              className="btn-secondary"
              onClick={() => navigate(isReadOnly ? '/parent-dashboard' : '/child-dashboard')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              ← Voltar ao Dashboard
            </button>
          </div>
          <span className="mission-subject-badge">{mission.subject}</span>
          <p>⭐ {mission.points_earned}/{mission.max_points} HeroCoins</p>
          <div className="print-actions-wrapper">
            <button
              className="btn-secondary"
              onClick={() => setShowPrintOptions((prev) => !prev)}
            >
              🖨️ Imprimir missão
            </button>

            {showPrintOptions && (
              <div className="print-options-panel">
                <h4>Selecione o que imprimir</h4>
                <label>
                  <input
                    type="checkbox"
                    checked={printOptions.summary}
                    onChange={() => togglePrintOption('summary')}
                  />
                  Conteúdo
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={printOptions.activities}
                    onChange={() => togglePrintOption('activities')}
                  />
                  Atividade avaliativa
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={printOptions.simulations}
                    onChange={() => togglePrintOption('simulations')}
                  />
                  Simulados
                </label>
                <div className="print-options-buttons">
                  <button className="btn-primary" onClick={handlePrintMission}>Imprimir</button>
                  <button className="btn-secondary" onClick={() => setShowPrintOptions(false)}>Fechar</button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="progress-stepper">
          <div 
            className={`step ${currentStep === 'summary' ? 'active' : ''} ${mission.summary_completed ? 'completed' : ''}`}
            onClick={() => setCurrentStep('summary')}
          >
            <div className="step-number">{mission.summary_completed ? '✓' : '1'}</div>
            <span className="step-label">Resumo</span>
          </div>
          <div 
            className={`step ${currentStep === 'activities' ? 'active' : ''} ${mission.activities_completed ? 'completed' : ''}`}
            onClick={() => setCurrentStep('activities')}
          >
            <div className="step-number">{mission.activities_completed ? '✓' : '2'}</div>
            <span className="step-label">Atividades</span>
          </div>
          <div 
            className={`step ${currentStep === 'simulation' ? 'active' : ''}`}
            onClick={() => setCurrentStep('simulation')}
          >
            <div className="step-number">3</div>
            <span className="step-label">Simulado</span>
          </div>
          <div 
            className={`step ${currentStep === 'report' ? 'active' : ''}`}
            onClick={() => setCurrentStep('report')}
          >
            <div className="step-number">4</div>
            <span className="step-label">Relatório</span>
          </div>
        </div>
        
        <div className="study-mission-content">
          {/* RESUMO */}
          {currentStep === 'summary' && (
            <div className="content-section summary-section">
              <h2>📖 Resumo do Conteúdo</h2>
              <div className="summary-content">
                <ReactMarkdown 
                  children={mission.generated_summary}
                  components={{
                    h1: ({children}) => <h1>{children}</h1>,
                    h2: ({children}) => <h2>{children}</h2>,
                    h3: ({children}) => <h3>{children}</h3>,
                    p: ({children}) => <p>{children}</p>,
                    strong: ({children}) => <strong>{children}</strong>,
                    ul: ({children}) => <ul>{children}</ul>,
                    ol: ({children}) => <ol>{children}</ol>,
                    li: ({children}) => <li>{children}</li>,
                  }}
                />
              </div>
              {!mission.summary_completed && !isReadOnly && (
                <button onClick={handleCompleteSummary} className="btn-primary">
                  ✅ Li e entendi o resumo
                </button>
              )}
              {mission.summary_completed && (
                <div className="success-message">
                  ✅ Resumo concluído! {!isReadOnly && 'Continue para as atividades.'}
                </div>
              )}
            </div>
        )}
        
        {/* ATIVIDADES */}
        {currentStep === 'activities' && !showActivityResult && (
          <div className="content-section activities-section">
            <h2>✏️ Atividades de Fixação</h2>
            
            {/* Modo Read-Only ou Atividades Completadas: Mostra feedback */}
            {mission.activities_completed && mission.activities_feedback && mission.activities_feedback.length > 0 ? (
              <>
                <div className="info-banner" style={{backgroundColor: '#e3f2fd', padding: '15px', borderRadius: '8px', marginBottom: '20px'}}>
                  📖 Visualizando suas respostas anteriores com a correção detalhada
                </div>
                
                {mission.activities.map((activity, idx) => {
                  const feedback = mission.activities_feedback[idx];
                  return (
                    <div key={activity.id} className={`feedback-card ${feedback?.is_correct ? 'correct' : 'incorrect'}`}>
                      <div className="feedback-header">
                        <span className="feedback-number">Questão {idx + 1}</span>
                        <span className={`feedback-status ${feedback?.is_correct ? 'correct' : 'incorrect'}`}>
                          {feedback?.is_correct ? '✅ Correto' : '❌ Incorreto'}
                        </span>
                      </div>
                      
                      <div className="feedback-body">
                        <p className="feedback-question">
                          <strong>
                            {activity.question || activity.statement || activity.sentence}
                          </strong>
                        </p>
                        
                        {activity.type === 'MULTIPLE_CHOICE' && activity.options && (
                          <div className="activity-options" style={{marginTop: '15px'}}>
                            {activity.options.map((opt, i) => {
                              const optLetter = opt.charAt(0);
                              const isUserAnswer = String(feedback?.user_answer).toUpperCase() === optLetter.toUpperCase();
                              const isCorrectAnswer = String(feedback?.correct_answer).toUpperCase() === optLetter.toUpperCase();
                              return (
                                <div 
                                  key={i} 
                                  className="activity-option"
                                  style={{
                                    backgroundColor: isCorrectAnswer ? '#d4edda' : (isUserAnswer ? '#f8d7da' : 'transparent'),
                                    border: isCorrectAnswer ? '2px solid #28a745' : (isUserAnswer ? '2px solid #dc3545' : '1px solid #ddd'),
                                    padding: '10px',
                                    borderRadius: '5px',
                                    marginBottom: '8px'
                                  }}
                                >
                                  <label>
                                    {isCorrectAnswer && '✅ '}
                                    {isUserAnswer && !isCorrectAnswer && '❌ '}
                                    {opt}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {activity.type === 'TRUE_FALSE' && (
                          <div className="activity-options" style={{marginTop: '15px'}}>
                            {[
                              { value: true, label: '✅ Verdadeiro' },
                              { value: false, label: '❌ Falso' }
                            ].map((option, i) => {
                              const isUserAnswer = feedback?.user_answer === option.value;
                              const isCorrectAnswer = feedback?.correct_answer === option.value;
                              return (
                                <div 
                                  key={i}
                                  className="activity-option"
                                  style={{
                                    backgroundColor: isCorrectAnswer ? '#d4edda' : (isUserAnswer ? '#f8d7da' : 'transparent'),
                                    border: isCorrectAnswer ? '2px solid #28a745' : (isUserAnswer ? '2px solid #dc3545' : '1px solid #ddd'),
                                    padding: '10px',
                                    borderRadius: '5px',
                                    marginBottom: '8px'
                                  }}
                                >
                                  <label>
                                    {isCorrectAnswer && '✅ '}
                                    {isUserAnswer && !isCorrectAnswer && '❌ '}
                                    {option.label}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        
                        {activity.type === 'FILL_BLANK' && (
                          <div className="activity-options" style={{marginTop: '15px'}}>
                            {Array.isArray(activity.alternatives) && activity.alternatives.length > 0 ? (
                              activity.alternatives.map((alt, i) => {
                                const isUserAnswer = String(feedback?.user_answer).toLowerCase() === String(alt).toLowerCase();
                                const isCorrectAnswer = String(feedback?.correct_answer).toLowerCase() === String(alt).toLowerCase();
                                return (
                                  <div 
                                    key={i}
                                    className="activity-option"
                                    style={{
                                      backgroundColor: isCorrectAnswer ? '#d4edda' : (isUserAnswer ? '#f8d7da' : 'transparent'),
                                      border: isCorrectAnswer ? '2px solid #28a745' : (isUserAnswer ? '2px solid #dc3545' : '1px solid #ddd'),
                                      padding: '10px',
                                      borderRadius: '5px',
                                      marginBottom: '8px'
                                    }}
                                  >
                                    <label>
                                      {isCorrectAnswer && '✅ '}
                                      {isUserAnswer && !isCorrectAnswer && '❌ '}
                                      {alt}
                                    </label>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="feedback-answers">
                                <p>
                                  <span className="answer-label">Sua resposta:</span>
                                  <span className={`answer-value ${feedback?.is_correct ? 'correct' : 'incorrect'}`}>
                                    {feedback?.user_answer !== null && feedback?.user_answer !== undefined && String(feedback?.user_answer).trim() !== ''
                                      ? String(feedback.user_answer)
                                      : '(não respondida)'}
                                  </span>
                                </p>
                                {!feedback?.is_correct && (
                                  <p>
                                    <span className="answer-label">Resposta correta:</span>
                                    <span className="answer-value correct">
                                      {feedback?.correct_answer !== null && feedback?.correct_answer !== undefined && String(feedback?.correct_answer).trim() !== ''
                                        ? String(feedback.correct_answer)
                                        : '(não informada)'}
                                    </span>
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {feedback?.explanation && (
                          <div className="feedback-explanation">
                            <strong>💡 Explicação:</strong> {feedback.explanation}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                <button onClick={() => navigate(isReadOnly ? '/parent-dashboard' : '/child-dashboard')} className="btn-primary">
                  ← Voltar ao Dashboard
                </button>
              </>
            ) : (
              /* Modo Normal: Permite responder */
              <>
            {mission.activities && mission.activities.map((activity, idx) => (
              <div key={activity.id} className="activity-card">
                <div className="activity-question">Questão {idx + 1}</div>
                
                {activity.type === 'MULTIPLE_CHOICE' && (
                  <div>
                    <p><strong>{activity.question}</strong></p>
                    <div className="activity-options">
                      {activity.options.map((opt, i) => (
                        <div key={i} className="activity-option">
                          <input
                            type="radio"
                            id={`activity-${activity.id}-${i}`}
                            name={`activity-${activity.id}`}
                            value={opt.charAt(0)}
                            onChange={(e) => setActivityAnswers({
                              ...activityAnswers,
                              [activity.id]: e.target.value
                            })}
                          />
                          <label htmlFor={`activity-${activity.id}-${i}`}>{opt}</label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {activity.type === 'TRUE_FALSE' && (
                  <div>
                    <p><strong>{activity.statement}</strong></p>
                    <div className="activity-options">
                      <div className="activity-option">
                        <input
                          type="radio"
                          id={`activity-${activity.id}-true`}
                          name={`activity-${activity.id}`}
                          value="true"
                          disabled={isReadOnly}
                          onChange={() => setActivityAnswers({
                            ...activityAnswers,
                            [activity.id]: true
                          })}
                        />
                        <label htmlFor={`activity-${activity.id}-true`}>✅ Verdadeiro</label>
                      </div>
                      <div className="activity-option">
                        <input
                          type="radio"
                          id={`activity-${activity.id}-false`}
                          name={`activity-${activity.id}`}
                          value="false"
                          disabled={isReadOnly}
                          onChange={() => setActivityAnswers({
                            ...activityAnswers,
                            [activity.id]: false
                          })}
                        />
                        <label htmlFor={`activity-${activity.id}-false`}>❌ Falso</label>
                      </div>
                    </div>
                  </div>
                )}
                
                {activity.type === 'FILL_BLANK' && (
                  <div>
                    <p><strong>{activity.question || activity.statement || activity.sentence || 'Complete a lacuna:'}</strong></p>
                    {Array.isArray(activity.alternatives) && activity.alternatives.length > 0 ? (
                      <div className="activity-options">
                        {activity.alternatives.map((alt, i) => (
                          <div key={i} className="activity-option">
                            <input
                              type="radio"
                              id={`activity-${activity.id}-${i}`}
                              name={`activity-${activity.id}`}
                              value={alt}
                              disabled={isReadOnly}
                              onChange={(e) => setActivityAnswers({
                                ...activityAnswers,
                                [activity.id]: e.target.value
                              })}
                            />
                            <label htmlFor={`activity-${activity.id}-${i}`}>{alt}</label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <input
                        type="text"
                        placeholder="Digite a palavra/expressão que completa a lacuna"
                        value={activityAnswers[activity.id] || ''}
                        disabled={isReadOnly}
                        onChange={(e) => setActivityAnswers({
                          ...activityAnswers,
                          [activity.id]: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: '8px',
                          border: '2px solid #ddd',
                          fontSize: '1rem',
                          marginTop: '10px'
                        }}
                      />
                    )}
                  </div>
                )}
                
                {activity.type === 'COMPLETE' && (
                  <div>
                    <p><strong>{activity.question}</strong></p>
                    <textarea
                      placeholder="Digite sua resposta..."
                      value={activityAnswers[activity.id] || ''}
                      disabled={isReadOnly}
                      onChange={(e) => setActivityAnswers({
                        ...activityAnswers,
                        [activity.id]: e.target.value
                      })}
                      style={{
                        width: '100%',
                        minHeight: '100px',
                        padding: '12px',
                        borderRadius: '8px',
                        border: '2px solid #ddd',
                        fontSize: '1rem',
                        marginTop: '10px',
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
            
            {!isReadOnly && (
              <button onClick={handleSubmitActivities} className="btn-primary">
                📝 Enviar Respostas
              </button>
            )}
            </>
            )}
          </div>
        )}
        
        {/* RESULTADO DAS ATIVIDADES */}
        {currentStep === 'activities' && showActivityResult && activityResult && (
          <div className="content-section simulation-result">
            <h2>📊 Resultado das Atividades</h2>
            
            <div className="result-summary">
              <div className="result-card">
                <div className="result-icon">✅</div>
                <div className="result-value">{activityResult.correct_count}/{activityResult.total_activities}</div>
                <div className="result-label">Acertos</div>
              </div>
              <div className="result-card">
                <div className="result-icon">🪙</div>
                <div className="result-value">+{activityResult.points_awarded}</div>
                <div className="result-label">HeroCoins</div>
              </div>
            </div>
            
            <h3 style={{marginTop: '30px', marginBottom: '20px'}}>📝 Correção Detalhada</h3>
            
            {activityResult.feedback && activityResult.feedback.map((item, idx) => (
              <div key={idx} className={`feedback-card ${item.is_correct ? 'correct' : 'incorrect'}`}>
                <div className="feedback-header">
                  <span className="feedback-number">Questão {idx + 1}</span>
                  <span className={`feedback-status ${item.is_correct ? 'correct' : 'incorrect'}`}>
                    {item.is_correct ? '✅ Correto' : '❌ Incorreto'}
                  </span>
                </div>
                
                <div className="feedback-body">
                  {mission.activities && mission.activities[idx] && (
                    <p className="feedback-question">
                      <strong>
                        {mission.activities[idx].question || 
                         mission.activities[idx].statement || 
                         mission.activities[idx].sentence}
                      </strong>
                    </p>
                  )}
                  
                  <div className="feedback-answers">
                    <p>
                      <span className="answer-label">Sua resposta:</span>
                      <span className={`answer-value ${item.is_correct ? 'correct' : 'incorrect'}`}>
                        {item.user_answer !== null && item.user_answer !== undefined ? String(item.user_answer) : '(não respondida)'}
                      </span>
                    </p>
                    {!item.is_correct && (
                      <p>
                        <span className="answer-label">Resposta correta:</span>
                        <span className="answer-value correct">{item.correct_answer}</span>
                      </p>
                    )}
                  </div>
                  
                  {item.explanation && (
                    <div className="feedback-explanation">
                      <strong>💡 Explicação:</strong> {item.explanation}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            <button onClick={handleCloseActivityResult} className="btn-primary" style={{marginTop: '20px'}}>
              ✅ Continuar para Simulados
            </button>
          </div>
        )}
        
        {/* SIMULADO */}
        {currentStep === 'simulation' && !selectedSimulation && (
          <div className="content-section simulation-section">
            <h2>🎯 Simulados</h2>
            
            <div className="simulation-list">
              {simulations.map((sim, idx) => (
                <div key={sim.id} className="simulation-card">
                  <div className="simulation-header">
                    <h3>Simulado {idx + 1}</h3>
                    <span className={`simulation-badge simulation-badge-${sim.difficulty.toLowerCase()}`}>
                      {sim.difficulty === 'EASY' ? 'Fácil' : sim.difficulty === 'INTERMEDIATE' ? 'Médio' : 'Difícil'}
                    </span>
                  </div>
                  {sim.status === 'COMPLETED' ? (
                    <div className="simulation-result">
                      <p>Nota: <strong>{sim.score}</strong></p>
                      <p>Acertos: {sim.correct_count}/{sim.total_questions}</p>
                      <button 
                        onClick={() => handleStartSimulation(sim)} 
                        className="btn-secondary"
                        style={{
                          marginTop: '10px',
                          backgroundColor: '#4CAF50',
                          borderColor: '#4CAF50'
                        }}
                      >
                        👁️ Revisar Respostas
                      </button>
                    </div>
                  ) : (
                    (
                      <button onClick={() => handleStartSimulation(sim)} className="btn-secondary">
                        {isReadOnly ? '👁️ Visualizar Simulado' : '▶️ Iniciar Simulado'}
                      </button>
                    )
                  )}
                </div>
              ))}
            </div>
            
            {mission.simulations_count < mission.max_simulations && (
              <div className="generate-simulation">
                <h3>Gerar Novo Simulado</h3>
                {isReadOnly && (
                  <p className="readonly-notice">
                    ℹ️ Você pode gerar simulados para visualização, mas não pode respondê-los.
                  </p>
                )}
                {generatingSimulation ? (
                  <div className="mission-loading-container mission-compact-loading">
                    <div className="mission-loading-spinner"></div>
                    <p>Gerando simulado... Aguarde, pode levar até 1 minuto.</p>
                  </div>
                ) : (
                  <div className="difficulty-buttons">
                    <button 
                      onClick={() => handleGenerateNewSimulation('EASY')} 
                      className="btn-secondary"
                      disabled={generatingSimulation}
                    >
                      😊 Fácil
                    </button>
                    <button 
                      onClick={() => handleGenerateNewSimulation('INTERMEDIATE')} 
                      className="btn-secondary"
                      disabled={generatingSimulation}
                    >
                      🤔 Médio
                    </button>
                    <button 
                      onClick={() => handleGenerateNewSimulation('HARD')} 
                      className="btn-secondary"
                      disabled={generatingSimulation}
                    >
                      🔥 Difícil
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* RESULTADO DO SIMULADO */}
        {currentStep === 'simulation' && showSimulationResult && simulationResult && (
          <div className="content-section simulation-result">
            <h2>📊 Resultado do Simulado</h2>
            
            <div className="result-summary">
              <div className="result-card">
                <div className="result-icon">🎯</div>
                <div className="result-value">{simulationResult.score}</div>
                <div className="result-label">Nota</div>
              </div>
              <div className="result-card">
                <div className="result-icon">✅</div>
                <div className="result-value">{simulationResult.correct_count}/{simulationResult.total_questions}</div>
                <div className="result-label">Acertos</div>
              </div>
              <div className="result-card">
                <div className="result-icon">🪙</div>
                <div className="result-value">+{simulationResult.points_awarded}</div>
                <div className="result-label">HeroCoins</div>
              </div>
            </div>
            
            <h3 style={{marginTop: '30px', marginBottom: '20px'}}>📝 Correção Detalhada</h3>
            
            {simulationResult.feedback && simulationResult.feedback.map((item, idx) => (
              <div key={idx} className={`feedback-card ${item.is_correct ? 'correct' : 'incorrect'}`}>
                <div className="feedback-header">
                  <span className="feedback-number">Questão {idx + 1}</span>
                  <span className={`feedback-status ${item.is_correct ? 'correct' : 'incorrect'}`}>
                    {item.is_correct ? '✅ Correto' : '❌ Incorreto'}
                  </span>
                </div>
                
                <div className="feedback-body">
                  {(() => {
                    const question = selectedSimulation?.questions?.[idx];
                    const userAnswerLabel = formatSimulationFeedbackAnswer(question, item.user_answer);
                    const correctAnswerLabel = formatSimulationFeedbackAnswer(question, item.correct_answer);

                    return (
                      <>
                  {selectedSimulation.questions && selectedSimulation.questions[idx] && (
                    <p className="feedback-question">
                      <strong>
                        {selectedSimulation.questions[idx].question || 
                         selectedSimulation.questions[idx].statement || 
                         selectedSimulation.questions[idx].sentence}
                      </strong>
                    </p>
                  )}
                  
                  <div className="feedback-answers">
                    <p>
                      <span className="answer-label">Sua resposta:</span>
                      <span className={`answer-value ${item.is_correct ? 'correct' : 'incorrect'}`}>
                        {userAnswerLabel}
                      </span>
                    </p>
                    {!item.is_correct && (
                      <p>
                        <span className="answer-label">Resposta correta:</span>
                        <span className="answer-value correct">{correctAnswerLabel}</span>
                      </p>
                    )}
                  </div>
                  
                  {item.explanation && (
                    <div className="feedback-explanation">
                      <strong>💡 Explicação:</strong> {item.explanation}
                    </div>
                  )}
                      </>
                    );
                  })()}
                </div>
              </div>
            ))}
            
            <button onClick={handleCloseSimulationResult} className="btn-primary" style={{marginTop: '20px'}}>
              ✅ Voltar aos Simulados
            </button>
          </div>
        )}
        
        {/* SIMULADO ATIVO */}
        {currentStep === 'simulation' && selectedSimulation && !showSimulationResult && (
          <div className="content-section simulation-active">
            <div className="simulation-active-header">
              {selectedSimulation.status === 'COMPLETED' && (
                <div style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  marginBottom: '10px',
                  textAlign: 'center',
                  fontWeight: '600'
                }}>
                  ✅ Simulado Concluído - Revisando Respostas
                </div>
              )}
              <h2>🎯 {selectedSimulation.difficulty === 'EASY' ? 'Simulado Fácil' : selectedSimulation.difficulty === 'INTERMEDIATE' ? 'Simulado Médio' : 'Simulado Difícil'}</h2>
              <button onClick={() => setSelectedSimulation(null)} className="btn-secondary">
                ← Voltar aos Simulados
              </button>
            </div>
            
            {!selectedSimulation.questions || selectedSimulation.questions.length === 0 ? (
              <div className="error-container">
                <p>⚠️ Não foi possível carregar as questões do simulado.</p>
                <button onClick={() => setSelectedSimulation(null)} className="btn-primary">
                  Voltar aos Simulados
                </button>
              </div>
            ) : (
              <>
                {selectedSimulation.questions.map((question, idx) => (
                  <div key={question.id || idx} className="activity-card">
                    <div className="activity-question">Questão {idx + 1}</div>
                    
                    {/* MÚLTIPLA ESCOLHA */}
                    {question.type === 'MULTIPLE_CHOICE' && (
                      <div>
                        <p><strong>{question.question}</strong></p>
                        <div className="activity-options">
                          {question.options && question.options.map((opt, i) => (
                            <div key={i} className="activity-option">
                              <input
                                type="radio"
                                id={`sim-${question.id || idx}-${i}`}
                                name={`sim-${question.id || idx}`}
                                value={opt.charAt(0)}
                                disabled={selectedSimulation.status === 'COMPLETED' || isReadOnly}
                                onChange={(e) => setSimulationAnswers({
                                  ...simulationAnswers,
                                  [question.id || idx]: e.target.value
                                })}
                              />
                              <label htmlFor={`sim-${question.id || idx}-${i}`}>{opt}</label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* VERDADEIRO OU FALSO */}
                    {question.type === 'TRUE_FALSE' && (
                      <div>
                        <p><strong>{question.statement || question.question}</strong></p>
                        <div className="activity-options">
                          <div className="activity-option">
                            <input
                              type="radio"
                              id={`sim-${question.id || idx}-true`}
                              name={`sim-${question.id || idx}`}
                              value="true"
                              disabled={selectedSimulation.status === 'COMPLETED' || isReadOnly}
                              onChange={() => setSimulationAnswers({
                                ...simulationAnswers,
                                [question.id || idx]: true
                              })}
                            />
                            <label htmlFor={`sim-${question.id || idx}-true`}>✅ Verdadeiro</label>
                          </div>
                          <div className="activity-option">
                            <input
                              type="radio"
                              id={`sim-${question.id || idx}-false`}
                              name={`sim-${question.id || idx}`}
                              value="false"
                              disabled={selectedSimulation.status === 'COMPLETED' || isReadOnly}
                              onChange={() => setSimulationAnswers({
                                ...simulationAnswers,
                                [question.id || idx]: false
                              })}
                            />
                            <label htmlFor={`sim-${question.id || idx}-false`}>❌ Falso</label>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* COMPLETAR (texto livre) */}
                    {question.type === 'COMPLETE' && (
                      <div>
                        <p><strong>{question.question}</strong></p>
                        <input
                          type="text"
                          placeholder="Digite sua resposta..."
                          disabled={selectedSimulation.status === 'COMPLETED' || isReadOnly}
                          value={simulationAnswers[question.id || idx] || ''}
                          onChange={(e) => setSimulationAnswers({
                            ...simulationAnswers,
                            [question.id || idx]: e.target.value
                          })}
                          style={{
                            width: '100%',
                            padding: '12px',
                            borderRadius: '8px',
                            border: '2px solid #ddd',
                            fontSize: '1rem',
                            marginTop: '10px'
                          }}
                        />
                      </div>
                    )}
                    
                    {/* COMPLETAR LACUNAS */}
                    {question.type === 'FILL_BLANK' && (
                      <div>
                        <p><strong>{question.sentence || question.question}</strong></p>
                        {Array.isArray(question.alternatives) && question.alternatives.length > 0 ? (
                          <div className="activity-options">
                            {question.alternatives.map((alt, i) => (
                              <div key={i} className="activity-option">
                                <input
                                  type="radio"
                                  id={`sim-${question.id || idx}-${i}`}
                                  name={`sim-${question.id || idx}`}
                                  value={alt}
                                  disabled={selectedSimulation.status === 'COMPLETED' || isReadOnly}
                                  onChange={(e) => setSimulationAnswers({
                                    ...simulationAnswers,
                                    [question.id || idx]: e.target.value
                                  })}
                                />
                                <label htmlFor={`sim-${question.id || idx}-${i}`}>{alt}</label>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <input
                            type="text"
                            placeholder="Digite a palavra/expressão que completa a lacuna"
                            disabled={selectedSimulation.status === 'COMPLETED' || isReadOnly}
                            value={simulationAnswers[question.id || idx] || ''}
                            onChange={(e) => setSimulationAnswers({
                              ...simulationAnswers,
                              [question.id || idx]: e.target.value
                            })}
                            style={{
                              width: '100%',
                              padding: '12px',
                              borderRadius: '8px',
                              border: '2px solid #ddd',
                              fontSize: '1rem',
                              marginTop: '10px'
                            }}
                          />
                        )}
                      </div>
                    )}
                    
                    {/* COMPLETAR (TEXTO LIVRE) */}
                    {question.type === 'COMPLETION' && (
                      <div>
                        <p><strong>{question.question}</strong></p>
                        <div className="activity-options">
                          <input
                            type="text"
                            style={{
                              width: '100%',
                              padding: '10px',
                              fontSize: '16px',
                              border: '2px solid #ddd',
                              borderRadius: '8px',
                              marginTop: '10px'
                            }}
                            placeholder="Digite sua resposta..."
                            disabled={selectedSimulation.status === 'COMPLETED' || isReadOnly}
                            onChange={(e) => setSimulationAnswers({
                              ...simulationAnswers,
                              [question.id || idx]: e.target.value
                            })}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                {selectedSimulation.status !== 'COMPLETED' && !isReadOnly && (
                  <button onClick={handleSubmitSimulation} className="btn-primary">
                    📝 Enviar Respostas
                  </button>
                )}
                {selectedSimulation.status === 'COMPLETED' && (
                  <div className="success-message" style={{marginTop: '20px'}}>
                    ✅ Este simulado já foi concluído. Você está revisando suas respostas para estudo.
                  </div>
                )}
              </>
            )}
          </div>
        )}
        
        {/* RELATÓRIO */}
        {currentStep === 'report' && (
          <div className="content-section report-section">
            <h2>📊 Seu Desempenho</h2>
            <div className="report-stats">
              <div className="stat-card">
                <div className="stat-icon">🏆</div>
                <div className="stat-value">{mission.points_earned}/{mission.max_points}</div>
                <div className="stat-label">Pontos Conquistados</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">⏱️</div>
                <div className="stat-value">{mission.time_spent_minutes}</div>
                <div className="stat-label">Minutos de Estudo</div>
              </div>
            </div>
            
            {isReadOnly ? (
              <button onClick={() => navigate('/parent-dashboard')} className="btn-primary">
                ← Voltar ao Dashboard
              </button>
            ) : (
              <button onClick={handleCompleteMission} className="btn-primary">
                🎉 Concluir Missão e Voltar
              </button>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

export default StudyMissionPlayer;
